import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import apiClient from '../../utils/axios';

const FETCH_TIMEOUT_MS = 15000;

const IntegrationsList = forwardRef(({ onEdit, onError }, ref) => {
  const [integrations, setIntegrations] = useState([]);
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  // Expose refresh method to parent
  useImperativeHandle(ref, () => ({
    refresh: () => setFetchKey(prev => prev + 1)
  }));

  useEffect(() => {
    const controller = new AbortController();
    let isTimeout = false;

    const timeoutId = setTimeout(() => {
      isTimeout = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [integrationsRes, typesRes] = await Promise.all([
          apiClient.get('/integrations/', { signal: controller.signal }),
          apiClient.get('/integrations/types', { signal: controller.signal })
        ]);

        clearTimeout(timeoutId);
        setIntegrations(integrationsRes.data.integrations || []);
        setIntegrationTypes(typesRes.data.integration_types || []);
      } catch (err) {
        clearTimeout(timeoutId);
        // Only set error if it was a timeout, not a cleanup abort
        if (isTimeout) {
          setError('Request timed out. Please try again later.');
        } else if (!controller.signal.aborted) {
          setError('Failed to load integrations');
        }
      } finally {
        if (!controller.signal.aborted || isTimeout) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchKey]);

  const handleRetry = useCallback(() => {
    setFetchKey(prev => prev + 1);
  }, []);

  const handleDelete = useCallback(async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    try {
      await apiClient.delete(`/integrations/${integrationId}`);
      setIntegrations(prev => prev.filter(i => i.integration_id !== integrationId));
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete integration';
      onError?.(errorMessage);
    }
  }, [onError]);

  const handleToggle = useCallback(async (integrationId, enabled) => {
    try {
      const response = await apiClient.patch(`/integrations/${integrationId}`, { enabled });
      const result = response.data;
      setIntegrations(prev => prev.map(i => 
        i.integration_id === integrationId 
          ? { ...i, enabled: result.integration.enabled }
          : i
      ));
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update integration';
      onError?.(errorMessage);
    }
  }, [onError]);

  const getIntegrationTypeName = useCallback((typeId) => {
    const type = integrationTypes.find(t => t.type_id === typeId);
    return type ? type.name : typeId;
  }, [integrationTypes]);

  // Loading placeholder
  if (loading) {
    return (
      <div className="dashboard-card">
        <div className="card-header">
          <h2>Your Integrations</h2>
        </div>
        <div className="integrations-list">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="integration-item integration-item-placeholder">
              <div className="integration-details">
                <div className="integration-header">
                  <div className="placeholder-text placeholder-title"></div>
                  <div className="placeholder-actions">
                    <div className="placeholder-toggle"></div>
                    <div className="placeholder-button-small"></div>
                    <div className="placeholder-button-small"></div>
                  </div>
                </div>
                <div className="placeholder-text placeholder-line"></div>
                <div className="placeholder-text placeholder-line"></div>
                <div className="placeholder-text placeholder-line-short"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-card">
        <div className="card-header">
          <h2>Your Integrations</h2>
        </div>
        <div className="list-error">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      <div className="card-header">
        <h2>Your Integrations</h2>
      </div>
      <div className="integrations-list">
        {integrations.length === 0 ? (
          <div className="no-data">No integrations configured</div>
        ) : (
          integrations.map(integration => (
            <div key={integration.integration_id} className="integration-item">
              <div className="integration-details">
                <div className="integration-header">
                  <strong>{integration.name}</strong>
                  <div className="integration-actions">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={integration.enabled}
                        onChange={(e) => handleToggle(
                          integration.integration_id, 
                          e.target.checked
                        )}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <button 
                      className="edit-btn"
                      onClick={() => onEdit(integration)}
                      title="Edit integration"
                    >
                      ✏️
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDelete(integration.integration_id)}
                      title="Delete integration"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="integration-type">
                  Type: {getIntegrationTypeName(integration.type_id)}
                </p>
                <p className="integration-endpoint">
                  Endpoint: {integration.api_endpoint || 'Default'}
                </p>
                <p className="webhooks-endpoint">
                  Webhooks Endpoint: {integration.webhook_endpoint || 'Not Supported'}
                </p>
                <div className="integration-status">
                  <span className={`status ${integration.enabled ? 'active' : 'inactive'}`}>
                    {integration.enabled ? 'Active' : 'Inactive'}
                  </span>
                  <span className="integration-date">
                    Created: {new Date(integration.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
});

IntegrationsList.displayName = 'IntegrationsList';

export default IntegrationsList;
